import Yup from '~/util/yup';
import {
	getAdmissionOptions,
	getOrdersByCertificationPeriodOptions,
	getInDraftOrderForUserOptions,
	getPatientPhysiciansOptions,
	startDraftOrderOptions,
	PatientPhysicianAssociationEnum
} from '@generated';
import { FormModal } from '~/shared/forms/FormModal';
import { useSuspenseQuery } from '@tanstack/react-query';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { DatePickerFormField } from '~/shared/forms/controls/fields/date-fields/DatePickerFormField';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';

const today = new Date();

const validationSchema = Yup.object().shape({
	certificationPeriodId: Yup.string().label('Certification Period').nullable().required(),
	referralAddendum: Yup.boolean().label('Referral Addendum'),
	orderingPatientPhysicianId: Yup.string().label('Ordering Physician').nullable().required(),
	orderDate: Yup.date().label('Order Date').nullable().required(),
	verbalOrder: Yup.boolean().label('Verbal Order').required()
});

export function StartOrderFormModal({
	stayId,
	certificationPeriodId,
	closeModal
}: {
	stayId: string;
	certificationPeriodId: string;
	closeModal: () => void;
}) {
	const { loggedInUser } = useIdentity();
	const { data: patientPhysicians } = useSuspenseQuery(getPatientPhysiciansOptions(stayId));
	const { data: admission } = useSuspenseQuery(getAdmissionOptions(stayId));

	const nonReferringAcceptingOrdersPhysicians = patientPhysicians.physicians.filter(x => x.canSignOrders);
	const referringPhysician = patientPhysicians.physicians.find(x => x.patientPhysicianAssociationId === PatientPhysicianAssociationEnum.Referring)!;
	return (
		<FormModal
			title="Start Draft Order"
			initialValues={{
				stayId,
				certificationPeriodId: certificationPeriodId,
				orderingPatientPhysicianId: null,
				verbalOrder: false,
				orderDate: today
			}}
			validationSchema={validationSchema}
			saveMutation={{
				...startDraftOrderOptions,
				invalidateQueryKeys: [
					getOrdersByCertificationPeriodOptions(certificationPeriodId).queryKey,
					getInDraftOrderForUserOptions(stayId, loggedInUser.id).queryKey
				]
			}}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			{formHelpers => {
				const onReferralAddendumChange = (referralAddendum: boolean) => {
					if (referralAddendum) {
						formHelpers.setFieldValue('orderingPatientPhysicianId', referringPhysician?.id);
					} else {
						formHelpers.setFieldValue('orderingPatientPhysicianId', null);
					}
				};
				const referralAddendum = formHelpers.values.referralAddendum;
				return (
					<div className="grid grid-cols-1 gap-6">
						<CheckboxFormField name="referralAddendum" hideLabel onChangePassthrough={onReferralAddendumChange} />
						{!referralAddendum ? (
							<SelectFormField
								name="orderingPatientPhysicianId"
								options={nonReferringAcceptingOrdersPhysicians.map(p => ({ id: p.id, name: p.physician.fullName }))}
								noOptionsMessage="No signing physicians found"
							/>
						) : (
							<span className="text-sm text-gray-500">Ordering Physician: {referringPhysician?.physician.fullName}</span>
						)}

						<DatePickerFormField name="orderDate" />
						<CheckboxFormField name="verbalOrder" hideLabel />
					</div>
				);
			}}
		</FormModal>
	);
}
